// Carry Roll GapChart — full-bag carry/total gapping + issue flags (6.3, 6.6).

import { AlertTriangle, Diamond } from 'lucide-react';
import { useStore } from '../../store';
import { buildBag } from '@carry-roll/shared-logic';
import { convertDistance } from '@carry-roll/shared-logic';

export function GapChart() {
  const bag = useStore((s) => s.bag);
  const distanceUnit = useStore((s) => s.distanceUnit);
  const { entries, issues } = buildBag(bag);

  if (entries.length === 0) {
    return (
      <div className="panel" data-testid="gap-chart" style={{ color: 'var(--color-text-muted)' }}>
        Your bag is empty. Add a club or load a swing-speed preset (amateur/pro).
      </div>
    );
  }

  const maxCarry = Math.max(...entries.map((e) => e.totalM), 1);
  const conv = (m: number) => convertDistance(m, distanceUnit);

  return (
    <div className="panel" data-testid="gap-chart">
      <h3 className="subsection-title">
        Gapping chart
      </h3>
      <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
        Bars show each club&apos;s carry. Overlaps (under 3 m apart) and holes (over 22 m apart) between adjacent clubs are flagged.
      </p>
      <div className="flex flex-col gap-2">
        {entries.map((e) => (
          <div key={e.clubName} className="flex items-center gap-2 text-sm">
            <span
              style={{
                width: '5rem',
                color: 'var(--color-text-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {e.clubName}
            </span>
            <div style={{ flex: 1, backgroundColor: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', height: 'var(--space-4)', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${(conv(e.carryM) / conv(maxCarry)) * 100}%`,
                  height: '100%',
                  backgroundColor: 'var(--color-carry)',
                }}
                aria-label={`${e.clubName} carry ${Math.round(conv(e.carryM))} ${distanceUnit}`}
              />
            </div>
            <span style={{ width: '4rem', textAlign: 'right' }}>
              {Math.round(conv(e.carryM))} {distanceUnit}
            </span>
          </div>
        ))}
      </div>
      {issues.length > 0 && (
        <div className="mt-3" data-testid="gap-issues">
          {issues.map((iss, i) => (
            <div
              key={i}
              className="badge mt-1"
              style={{
                borderColor: iss.type === 'overlap' ? 'var(--color-warn)' : 'var(--color-danger)',
                display: 'block',
              }}
            >
              {iss.type === 'overlap' ? (
                <AlertTriangle size={12} aria-hidden="true" style={{ color: 'var(--color-warn)', verticalAlign: 'middle' }} />
              ) : (
                <Diamond size={12} aria-hidden="true" style={{ color: 'var(--color-danger)', verticalAlign: 'middle' }} />
              )}{' '}
              {iss.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
